"use client";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ProductsContext = createContext();

export const ProductsProvider = ({ children }) => {
  const [products, setProducts]             = useState([]);
  const [returnPolicies, setReturnPolicies] = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);

  const PRODUCTS_ENDPOINT = `${BASE_URL}/products`;
  const { token, logout } = useAuth();

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  });

  const handleAuthError = (res) => {
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized");
    }
  };

  const safeParseArray = (value) => {
    try {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  const safeParseJSON = (value) => {
    try {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  const resolveUrl = (path) => {
    if (!path) return "";
    const cleaned = String(path).trim();
    return cleaned.startsWith("http") ? cleaned : `${CDN_URL}/${cleaned}`;
  };

  // Helper: coerce a value to a non-null string, returning "" for null/undefined/"null"
  const s = (v) => (v == null || String(v) === "null") ? "" : String(v);

  const normalizeProduct = (p = {}) => {
    const num = (v) => {
      const n = Number(String(v ?? "").trim());
      return isNaN(n) ? 0 : n;
    };

    // ── Product Details ──────────────────────────────────────────────────────
    let rawDetails = [];
    if (Array.isArray(p.descriptions))         rawDetails = p.descriptions;
    else if (Array.isArray(p.product_details)) rawDetails = p.product_details;
    else if (p.description)                    rawDetails = safeParseJSON(p.description);

    const productDetails = rawDetails.map((d) => ({
      title:   d.title   || "",
      content: d.content || "",
      image:   d.image   || null,
      preview: d.image   ? resolveUrl(d.image) : null,
    }));

    // ── Variants ─────────────────────────────────────────────────────────────
   const variations = (p.variations || []).map((v) => {
  // Try _id fields first, then fallback to direct fields (API may return numeric IDs directly)
  const colorId    = s(v.color_id    || v.color    || "");
  const materialId = s(v.material_id || v.material || "");
  const sizeId     = s(v.size_id     || v.size     || "");
  const shapeId    = s(v.shape_id    || v.shape    || "");

  // Price: prefer offer_price if price is missing
  const price = s(v.price || v.offer_price || "");

  return {
    id:      v.id != null ? String(v.id) : null,
    title:   v.title || v.name || v.value || "",
    image:   null,
    preview: v.image ? resolveUrl(v.image) : null,

    color_id:    colorId,
    material_id: materialId,
    size_id:     sizeId,
    shape_id:    shapeId,

    // Flat alias — what dropdowns read
    color:    colorId,
    material: materialId,
    size:     sizeId,
    shape:    shapeId || s(v.item_shape),

    // Display names
    color_name:    s(v.color_name    || v.color),
    material_name: s(v.material_name || v.material),
    size_name:     s(v.size_name     || v.size),
    shape_name:    s(v.item_shape    || v.shape_name),
    color_code:    s(v.color_code),

    price,
    offer_price: s(v.offer_price || ""),
    quantity:    v.quantity != null ? String(v.quantity) : "",
    stock:       v.stock    != null ? String(v.stock)    : "",
    sku:         v.sku      || "",
  };
});
  const bulk_offers = (p.bulk_offers || []).map((o) => ({
  id:             o.id,
  product_id:     o.product_id,
  min_quantity:   o.min_quantity   != null ? Number(o.min_quantity)   : 0,
  max_quantity:   o.max_quantity   != null ? Number(o.max_quantity)   : null,   // ← add this
  discount_type:  o.discount_type  || "fixed",
  discount_value: o.discount_value != null ? Number(o.discount_value) : 0,
  status:         o.status         || "Active",
}));

    // ── Return policy ────────────────────────────────────────────────────────
    const returnPolicyId = p.return_policy != null && p.return_policy !== ""
      ? String(p.return_policy)
      : "";

    const returnStatus =
      String(p.return_status || "").trim().toLowerCase() === "non_returnable"
        ? "non_returnable"
        : "returnable";

    return {
      id:               p.id,
      name:             p.product_name   || "",
      brand_name:       p.brand_name     || "",
      description:      p.description   || "",
      prod_description: p.prod_description || "",
      manufacturer:     p.manufacturer  || "",
      item_type_name:   p.item_type_name || "",
      frame_color:      p.frame_color   || "",
      frame_material:   p.frame_material || "",
      frame_type:       p.frame_type    || "",
      item_shape:       p.item_shape    ? String(p.item_shape) : "",
      // Product-level color/material/size: API stores numeric IDs here directly
      color:            p.color         ? String(p.color)     : "",
      material:         p.material      ? String(p.material)  : "",
      model_name:       p.model_name    || "",

      // ── FIX: product-level `size` is free text (e.g. "gfhfg", "343*344"),
      // NOT a foreign key. The real lookup id lives in a *separate* API
      // field, `size_id` (see product 104: size:"gfhfg", size_id:"5").
      // The old code only ever mapped `p.size` and silently dropped
      // `p.size_id`, so AddProductPage's `pick(product, ['size_id','size'])`
      // never found a size_id to read and fell back to the free-text value,
      // which resolveStrictLookupId correctly (and necessarily) rejects —
      // leaving the Size dropdown blank in edit mode even though Postman/the
      // API clearly returns size_id. Carrying size_id through here fixes it.
      size:             p.size          ? String(p.size)      : "",
      size_id:          p.size_id       ? String(p.size_id)   : "",

      style:            p.style         || "",
      product_type:     p.product_type  ? String(p.product_type) : "",
      return_policy:    returnPolicyId,
      return_status:    returnStatus,
      product_policy:   p.product_policy || "",
      no_of_items:      p.no_of_items   != null ? String(p.no_of_items) : "1",

      color_name:  p.color_name  || "",
      color_code:  p.color_code  || "",

      // Pricing
      your_price:               num(p.your_price),
      max_retail_price:         num(p.max_retail_price),
      min_seller_allowed_price: num(p.min_seller_allowed_price),
      max_seller_allowed_price: num(p.max_seller_allowed_price),
      sale_price:               num(p.sale_price),
      price:                    num(p.sale_price),
      originalPrice:            num(p.offer_price),

      // Stock / qty
      stock:              num(p.quantity),
      quantity:           num(p.quantity),
      min_order_qty:      p.min_order_qty != null && p.min_order_qty !== ""
                            ? String(p.min_order_qty) : "1",
      max_order_quantity: p.max_order_quantity != null ? String(p.max_order_quantity) : "",

      // Category hierarchy
      categoryId:       String(p.category_id      || ""),
      category_name:    String(p.category_name    || ""),
      subCategoryId:    String(p.subcategory_id   || ""),
      subcategory_name: String(p.subcategory_name || ""),
      modalCategoryId:  p.model_category_id ? String(p.model_category_id) : "",

      // Identifiers
      sku:          p.sku    || "",
      status:       p.status === "Active" ? "Active" : "Inactive",
      admin_status: p.admin_status || "",

      // Units & counts
      unit_count:      p.unit_count      != null ? String(p.unit_count) : "",
      unit_count_type: p.unit_count_type || "",

      // Product dimensions
      item_weight:             p.item_weight             || "",
      item_weight_unit:        p.item_weight_unit        || "kg",
      item_length_longer_edge: p.item_length_longer_edge || "",
      item_length_unit:        p.item_length_unit        || "cm",
      item_width_shorter_edge: p.item_width_shorter_edge || "",
      item_width_unit:         p.item_width_unit         || "cm",
      item_depth:              p.item_depth              || "",

      // Package dimensions
      item_package_length:  p.item_package_length  || "",
      package_length_unit:  p.package_length_unit  || "cm",
      item_package_width:   p.item_package_width   || "",
      package_width_unit:   p.package_width_unit   || "cm",
      item_package_height:  p.item_package_height  || "",
      package_height_unit:  p.package_height_unit  || "cm",
      item_package_weight:  p.item_package_weight  || "",
      package_weight_unit:  p.package_weight_unit  || "kg",

      // Pricing extras
      prod_tax_code:         p.prod_tax_code         || "",
      sale_start_date:       p.sale_start_date       || "",
      sale_end_date:         p.sale_end_date         || "",
      offering_relase_date:  p.offering_relase_date  || "",
      merchant_release_date: p.merchant_release_date || "",
      offering_message:      p.offering_message      || "",
      wrap_available:
        p.wrap_available === "1" ||
        p.wrap_available === 1   ||
        p.wrap_available === true,
      is_customizable: p.customisations === "Yes",

      // Media
      images: safeParseArray(p.product_images).map(resolveUrl),
      video:  p.video ? resolveUrl(p.video) : "",
      tags:   Array.isArray(p.tags)
        ? p.tags
        : p.tags
          ? String(p.tags).split(",").map((t) => t.trim()).filter(Boolean)
          : [],

      // SEO
      meta_title:       p.meta_title       || "",
      meta_description: p.meta_description || "",
      slug:             p.slug             || "",

      // Relations
      productDetails,
      variations,
      bulk_offers,
    };
  };

  // ── Fetch products ──────────────────────────────────────────────────────────
  const refreshProducts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(PRODUCTS_ENDPOINT, { headers: getHeaders() });
      handleAuthError(res);
      const json = await res.json();
      const normalized = Array.isArray(json?.data)
        ? json.data.map(normalizeProduct)
        : [];
      setProducts(normalized);

      // Build returnPolicies from actual API data
      const policyMap = new Map();
      if (Array.isArray(json?.data)) {
        json.data.forEach((p) => {
          if (p.return_policy != null && p.return_policy !== "" && p.product_policy) {
            policyMap.set(String(p.return_policy), p.product_policy);
          }
        });
      }
      setReturnPolicies(
        Array.from(policyMap.entries()).map(([id, policy_name]) => ({ id, policy_name }))
      );
    } catch (err) {
      setError("Failed to load products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ── Add product ─────────────────────────────────────────────────────────────
  const addProduct = async (formData) => {
    try {
      const res = await fetch(PRODUCTS_ENDPOINT, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        body: formData,
      });
      const contentType = res.headers.get("content-type");
      let json = null;
      if (contentType?.includes("application/json")) json = await res.json();

      if (res.status === 401)
        return { success: false, status: 401, message: "Unauthorized. Please login again." };
      if (res.status === 422)
        return { success: false, status: 422, message: "Validation failed", errors: json?.errors || {} };
      if (res.status === 200 || res.status === 201) {
        await refreshProducts();
        return { success: true, status: res.status, data: json?.data };
      }
      return { success: false, status: res.status, message: json?.message || "Operation failed" };
    } catch (err) {
      return { success: false, message: "Network error. Please try again." };
    }
  };

  // ── Update product ──────────────────────────────────────────────────────────
  const updateProduct = async (id, payload) => {
    try {
      const isFormData = payload instanceof FormData;
      const res = await fetch(`${PRODUCTS_ENDPOINT}/${id}`, {

        method: "POST",
        headers: isFormData
          ? { Authorization: `Bearer ${token}`, Accept: "application/json" }
          : getHeaders(),
        body: isFormData ? payload : JSON.stringify(payload),
      });
      handleAuthError(res);
      const contentType = res.headers.get("content-type");
      let json = null;
      if (contentType?.includes("application/json")) json = await res.json();

      if (res.status === 401)
        return { success: false, status: 401, message: "Unauthorized. Please login again." };
      if (res.status === 422)
        return { success: false, status: 422, message: "Validation failed", errors: json?.errors || {} };
      if (res.status === 200 || res.status === 201) {
        await refreshProducts();
        return { success: true, status: res.status, data: json?.data };
      }
      return { success: false, status: res.status, message: json?.message || "Operation failed" };
    } catch (err) {
      return { success: false, message: "Network error while updating product" };
    }
  };

  // ── Toggle status ───────────────────────────────────────────────────────────
  const toggleProductStatus = async (id) => {
    const product = products.find((p) => p.id === id);
    if (!product) return;
    const newStatus = product.status === "Active" ? "Inactive" : "Active";
    try {
      const res = await fetch(`${PRODUCTS_ENDPOINT}/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (res.ok) {
        setProducts((prev) =>
          prev.map((p) => p.id === id ? { ...p, status: newStatus } : p)
        );
        return { success: true, data: { ...product, status: newStatus } };
      }
      return { success: false, message: json.message };
    } catch (err) {
      return { success: false, message: "Network error" };
    }
  };

  // ── Delete product ──────────────────────────────────────────────────────────
  const deleteProduct = async (id) => {
    try {
      const deleteAuditId = localStorage.getItem("admin_id");
      const res = await fetch(`${PRODUCTS_ENDPOINT}/${id}`, {
        method: "DELETE",
        headers: { ...getHeaders(), Accept: "application/json" },
        body: JSON.stringify({ delete_audit_id: deleteAuditId }),
      });
      handleAuthError(res);
      if (res.ok) {
        setProducts((prev) => prev.filter((p) => String(p.id) !== String(id)));
      } else {
        const json = await res.json();
        alert(json?.message || "Delete failed");
      }
    } catch (err) {
      alert("Something went wrong");
    }
  };

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const value = useMemo(
    () => ({
      products,
      loading,
      error,
      returnPolicies,
      refreshProducts,
      addProduct,
      updateProduct,
      deleteProduct,
      toggleProductStatus,
    }),
    [products, loading, error, returnPolicies]
  );

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used inside ProductsProvider");
  return ctx;
};